<script setup lang="ts">
  import DateTimePicker from '@/components/form/DateTimePicker.vue'
  import ProductExtraOptions from '@/components/ProductExtraOptions.vue'
  import PlusIcon from '@/icons/plus-icon.vue'
  import { type CreateJourneyForm } from '@/model/create-journey.model.ts'
  import { useDriversStore } from '@/store/driver.store'
  import { useJourneysStore } from '@/store/journey.store'
  import {
    AANGEKOMEN_LABEL,
    FINAAL_LABEL,
    type JourneyTemplate,
    type StatusOption,
  } from '@/types/journey-template.ts'
  import type { Product } from '@/types/product'
  import { setTouched as setTouchedProducts } from '@/utils/product-utils'
  import {
    createEmptyJourneyProduct,
    createJourney,
    setTouched,
    validateCreateJourney,
  } from '@/utils/create-journey'
  import {
    DropDown,
    FormFieldContainer,
    FormInput,
    Modal,
    require,
    UiButton,
    useToastStore,
  } from '@logistiek-zonder-papier/lzp-library'
  import type { Ref } from 'vue'
  import { computed, onMounted, ref } from 'vue'
  import { useLegacyAppStore } from '@/store/legacy-app.store'

  const appStore = useLegacyAppStore()
  const toastStore = useToastStore()
  const journeysStore = useJourneysStore()
  const driversStore = useDriversStore()

  const show: Ref<boolean> = ref(false)
  const journeyTemplate: Ref<JourneyTemplate | null> = ref(null)
  const formData = ref<CreateJourneyForm>(
    createJourney(appStore.appInitData?.user, journeyTemplate.value)
  )
  const isSubmitting = ref(false)

  const errors = computed(() => validateCreateJourney(formData.value, journeyTemplate.value))

  onMounted(async () => {
    await driversStore.fetchAllDriversFromOrganisation()
  })

  const statusOptions: StatusOption[] = [AANGEKOMEN_LABEL, FINAAL_LABEL]

  const carriers = computed(() => journeyTemplate.value?.stroomVervoerder ?? [])
  const products = computed(() => journeyTemplate.value?.materialen ?? [])

  function toggleModal() {
    show.value = !show.value

    if (show.value) {
      formData.value = createJourney(appStore.appInitData?.user, journeyTemplate.value)

      isSubmitting.value = false

      touched.value = setTouched(false)
      touchedProducts.value = []
    }
  }

  function setJourneyTemplate(data: JourneyTemplate) {
    journeyTemplate.value = data
    formData.value = createJourney(appStore.appInitData?.user, journeyTemplate.value)
    if (data.stroomVervoerder && data.stroomVervoerder.length === 1) {
      formData.value.carrierOrganisation = data.stroomVervoerder?.[0] ?? null
    }
  }

  const canAddProduct = computed(() => {
    const current = formData.value.products.length
    const available = products.value.length
    return current < available
  })

  const touched = ref<Record<keyof CreateJourneyForm, boolean>>(setTouched(false))
  const touchedProducts = ref<Record<keyof Product, boolean>[]>([])

  function fieldError(field: keyof CreateJourneyForm) {
    if (!isSubmitting.value && !touched.value[field]) return undefined
    return errors.value.formErrors[field]
  }

  async function submitNewJourneyForm() {
    isSubmitting.value = true
    if (
      Object.values(errors.value.formErrors).some(error => error != null) ||
      Object.values(errors.value.productErrors).some(error => error != null)
    ) {
      touched.value = setTouched(true)
      for (let index = 0; index < touchedProducts.value.length; index++) {
        touchedProducts.value[index] = setTouchedProducts(true)
      }

      toastStore.error('Er zijn één of meerdere velden verkeerd ingevuld.')
    } else {
      try {
        await journeysStore.submitNewJourneyForm(
          formData.value,
          require(journeyTemplate.value, 'JourneyTemplate is not set')
        )
      } catch (error) {
        toastStore.error('Er is iets fout gegaan probeer het later opnieuw')
        isSubmitting.value = false
        throw error
      }
      toastStore.success('Rit successvol aangemaakt')
      toggleModal()
    }

    isSubmitting.value = false
  }

  defineExpose({ toggleModal, setJourneyTemplate })
</script>

<template>
  <modal v-model:show="show">
    <template #header>
      <h3>Nieuwe rit</h3>
    </template>

    <div class="form-modal-info">
      <span>kenmerk:</span>
      <p>{{ journeyTemplate?.stroomName || 'geen kenmerk' }}</p>

      <span>Producent:</span>
      <p>{{ journeyTemplate?.stroomVerwerker?.organisatieNaam || 'geen producent' }}</p>
    </div>

    <DropDown
      v-model="formData.carrierOrganisation"
      :data="carriers"
      label="Vervoerders organisatie"
      :required="true"
      :error="fieldError('carrierOrganisation')"
      @blur="touched['carrierOrganisation'] = true"
    >
      <template #empty>Selecteer een vervoerder</template>
      <template #item="{ item }">
        <div class="selection-option flex-container">
          <span class="item-name">
            {{ item?.organisatieNaam }}
          </span>
          <span class="item-name">
            {{ item?.organisatieNummer }}
          </span>
          <span class="item-name">
            {{ item?.straatnaam }} {{ item?.huisNummer }} {{ item?.toevoeging }}
          </span>
        </div>
      </template>
    </DropDown>

    <DropDown
      v-model="formData.carrierDriver"
      :data="driversStore.drivers"
      label="Kenteken"
      :enable-search="true"
      :required="true"
      :error="fieldError('carrierDriver')"
      @blur="touched['carrierDriver'] = true"
    >
      <template #empty>Selecteer een kenteken</template>
      <template #item="{ item }">
        <div class="selection-option flex-container">
          <span class="item-name">
            {{ item?.registration.licenseplate }}
          </span>
        </div>
      </template>
    </DropDown>

    <FormInput
      v-model="formData.remark"
      label="Opmerking"
      placeholder="Vul Opmerking in"
      :required="true"
      :maxLength="255"
      :error="fieldError('remark')"
      @blur="touched['remark'] = true"
    />

    <DateTimePicker
      v-model="formData.startDate"
      label="Start tijd"
      :required="true"
      :error="fieldError('startDate')"
      @blur="touched['startDate'] = true"
    />

    <DateTimePicker
      v-model="formData.endDate"
      label="Eind tijd"
      :required="true"
      :error="fieldError('endDate')"
      @blur="touched['endDate'] = true"
    />

    <DropDown
      v-model="formData.status"
      :data="statusOptions"
      label="Status"
      :required="true"
      :error="fieldError('status')"
      @blur="touched['status'] = true"
    >
      <template #empty>Selecteer een rit status</template>
      <template #item="{ item }">
        <div class="selection-option flex-container">
          <span class="item-name">{{ item.label }}</span>
        </div>
      </template>
    </DropDown>

    <FormFieldContainer label="Producten" :error="fieldError('products')" :optional="false">
      <ProductExtraOptions
        v-for="(_, index) of formData.products"
        :key="index"
        v-model="formData.products[index]!"
        v-model:touched="touchedProducts[index]!"
        :errors="errors.productErrors[index] ?? {}"
        :selectedProducts="formData.products"
        :optionalFields="[]"
        :disabledFields="[]"
        :allProducts="products"
        :required="false"
        @remove="
          () => {
            formData.products.splice(index, 1)
            touchedProducts.splice(index, 1)
          }
        "
      />
    </FormFieldContainer>

    <div class="add-product" v-if="canAddProduct">
      <button
        @click="
          () => {
            formData.products.push(createEmptyJourneyProduct())
            touchedProducts.push(setTouchedProducts(false))
          }
        "
      >
        <span>
          <PlusIcon />
        </span>
      </button>
    </div>

    <template #footer="{ close }">
      <UiButton variant="default" type="submit" label="Annuleren" @click="close" />
      <UiButton
        variant="prominent"
        type="submit"
        label="Opslaan"
        :disabled="isSubmitting"
        @click="submitNewJourneyForm"
      />
    </template>
  </modal>
</template>

<style scoped>
  .add-product {
    display: flex;
    gap: var(--spacing-xs);
    margin-top: var(--spacing-xs);

    button {
      margin-left: auto;
      width: var(--components-component-height);
      height: var(--components-component-height);
      border-radius: var(--components-form-field-corners);
      border: 1px solid var(--colours-grey-3);
      background: var(--colours-grey-1);
    }

    button:hover {
      background: var(--colours-support-hover);
    }
  }

  .form-modal-info {
    display: grid;
    grid-template-columns: auto 1fr;
    gap: var(--spacing-xs);
  }

  .form-modal-info p {
    margin: 0;
    margin-bottom: var(--spacing-xs);
  }

  .selection-option {
    display: flex;
    justify-content: space-between;
    gap: var(--spacing-xs);
  }

  .selection-option .item-name {
    flex: 1;
  }

  .button {
    font-size: 1rem;
    font-style: normal;
    font-weight: 600;
    line-height: 1.5rem;
    height: var(--components-component-height);
    padding: 0 var(--spacing-m);
    justify-content: center;
    border-radius: var(--components-component-corners);
  }

  .button-confirm {
    color: var(--colours-grey-1);
    background: var(--colours-brand-colour-1);
  }

  .button-cancel {
    color: var(--colours-grey-5);
    border: 1px solid var(--colours-grey-3);
    background: var(--colours-grey-1);
  }
</style>
